// Inline-SVG donut chart for proportional breakdowns. Center holds
// the headline total; legend below maps slices to colors.
//
// Implementation note: each slice is a stroked arc on the same
// circle, using stroke-dasharray to draw exactly the right span.
// pathLength=100 normalizes everything so dasharray values are
// percentages.

export type Slice = { label: string; value: number; color: string };

const SIZE = 140;
const STROKE = 16;
const RADIUS = (SIZE - STROKE) / 2;

export function Donut({
  slices,
  label,
  centerLabel,
}: {
  slices: Slice[];
  label: string;
  /** Optional override for the center stat line; defaults to "total" */
  centerLabel?: string;
}) {
  const total = slices.reduce((acc, s) => acc + s.value, 0);
  // Track-circle (background) so empty / zero data still renders a
  // recognizable ring outline.
  const trackColor = "rgb(var(--ink-800) / 1)";

  // Walk slices and accumulate offsets. dasharray="span 100" draws
  // exactly span% of the circumference; dashoffset rotates the start.
  let cursor = 0;

  return (
    <div className="border border-ink-800 rounded-md p-4 bg-ink-900/30 flex flex-col items-center">
      <span className="self-start font-mono text-[10px] uppercase tracking-widest text-ink-400 mb-2">
        {label}
      </span>
      <div className="relative">
        <svg
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          width={SIZE}
          height={SIZE}
          aria-label={label}
        >
          {/* track */}
          <circle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={RADIUS}
            fill="none"
            stroke={trackColor}
            strokeWidth={STROKE}
            pathLength={100}
          />
          {/* slices */}
          {total > 0 &&
            slices.map((s) => {
              const span = (s.value / total) * 100;
              const offset = cursor;
              cursor += span;
              return (
                <circle
                  key={s.label}
                  cx={SIZE / 2}
                  cy={SIZE / 2}
                  r={RADIUS}
                  fill="none"
                  stroke={s.color}
                  strokeWidth={STROKE}
                  pathLength={100}
                  strokeDasharray={`${span} ${100 - span}`}
                  // Negative offset rotates dasharray start.
                  strokeDashoffset={-offset}
                  // Rotate -90deg so slice 0 starts at 12 o'clock.
                  transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
                  opacity={s.value > 0 ? 1 : 0}
                >
                  <title>{`${s.label}: ${s.value}`}</title>
                </circle>
              );
            })}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <span className="text-2xl font-medium tabular-nums text-ink-50">
            {total}
          </span>
          {centerLabel && (
            <span className="font-mono text-[9px] uppercase tracking-widest text-ink-400 mt-0.5">
              {centerLabel}
            </span>
          )}
        </div>
      </div>
      <ul className="mt-3 w-full space-y-0.5 text-xs">
        {slices.map((s) => (
          <li key={s.label} className="flex items-center gap-2">
            <span
              aria-hidden
              className="inline-block h-2 w-2 rounded-sm"
              style={{ backgroundColor: s.color }}
            />
            <span className="text-ink-300 flex-1">{s.label}</span>
            <span className="tabular-nums text-ink-100">{s.value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
